import React, {useState} from "react";
import {
  createMerchantOrder,
  formatMerchantApiError,
  MerchantCreateOrderBody,
} from "../../api/merchant";
import {useAuth} from "../../auth";
import {navigate} from "../../hooks";
import {rublesToKopecks} from "../../utils/amount";
import {
  PortalInAmountMode,
  PortalInTermsField,
  parseTransferCount,
  portalInTermsHint,
  toPortalInTerms,
  validatePortalInTerms,
} from "../../utils/portalInTerms";
import {createMerchantPortalIn, MerchantPortalInQuote, quoteMerchantPortalIn} from "../../api/merchantPortalIn";
import {ApiError} from "../../api/client";

export type MerchantCreateOrderBank = "any" | "sber" | "tinkoff" | "sbp";
export type MerchantCreateRequisiteType = "card" | "phone";

export interface MerchantCreateOrderValues {
  /**
   * Сумма в рублях, как её ввёл пользователь: "1000.50". При диапазоне — его
   * верхняя граница («до»).
   */
  amount: string;
  bank: MerchantCreateOrderBank;
  requisiteType: MerchantCreateRequisiteType;
  /** Номер карты (16 цифр) или телефон в формате +79991234567. */
  requisite: string;
  holderName: string;
  recipientBank: string;
  side?: "in" | "out";
  /** Только для пополнения: точная сумма или диапазон, из которого сумму выберет трейдер. */
  amountMode: PortalInAmountMode;
  /** Нижняя граница диапазона («от»), рубли; читается только при диапазоне. */
  amountMin: string;
  /** Количество переводов, строкой как набрано; по умолчанию один. */
  transferCount: string;
  /** Минимальная сумма одного перевода, рубли; читается при двух переводах и больше. */
  minTransfer: string;
}

interface MerchantCreateOrderFormProps {
  onSubmit?: (values: MerchantCreateOrderValues) => void;
  submitting?: boolean;
  serverError?: string;
  quote?: MerchantPortalInQuote | null;
  onValuesChange?: (values: MerchantCreateOrderValues) => void;
  locked?: boolean;
  retrying?: boolean;
}

type MerchantCreateErrors = Partial<Record<"amount" | "requisite" | PortalInTermsField, string>>;

const AMOUNT_PATTERN = /^\d+([.,]\d{1,2})?$/;
// Ровно 16: normalizeCard на бэкенде принимает только 16 цифр, и форма не
// должна пропускать вперёд то, что сервер всё равно отобьёт четырёхсотым.
const CARD_PATTERN = /^\d{16}$/;
const PHONE_PATTERN = /^\+?7\d{10}$/;

const EMPTY_VALUES: MerchantCreateOrderValues = {
  amount: "",
  bank: "any",
  requisiteType: "card",
  requisite: "",
  holderName: "",
  recipientBank: "",
  side: "out",
  amountMode: "exact",
  amountMin: "",
  transferCount: "1",
  minTransfer: "",
};

export function MerchantCreateOrderForm({
  onSubmit,
  submitting = false,
  serverError = "",
  quote = null,
  onValuesChange,
  locked = false,
  retrying = false,
}: MerchantCreateOrderFormProps): React.JSX.Element {
  const [values, setValues] = useState<MerchantCreateOrderValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<MerchantCreateErrors>({});

  const patch = (part: Partial<MerchantCreateOrderValues>): void => {
    if (locked) return;
    const next = {...values, ...part};
    onValuesChange?.(next);
    setValues(next);
  };

  const clearError = (field: keyof MerchantCreateErrors): void => {
    setErrors((current) => (current[field] ? {...current, [field]: ""} : current));
  };

  const pickRequisiteType = (requisiteType: MerchantCreateRequisiteType): void => {
    patch({requisiteType, requisite: ""});
    clearError("requisite");
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (submitting) return;
    const problems = validateCreateOrder(values);
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;
    onSubmit?.(values);
  };

  const isCard = values.requisiteType === "card";
  const isIn = values.side === "in";
  const isRange = isIn && values.amountMode === "range";
  const transferCount = parseTransferCount(values.transferCount);
  const needsMinTransfer = isIn && transferCount !== null && transferCount >= 2;
  const termsHint = isIn ? portalInTermsHint(values) : "";
  const quoteIsRange = Boolean(quote?.usdtAtomicMin);

  const pickAmountMode = (amountMode: PortalInAmountMode): void => {
    patch({amountMode});
    clearError("amountMin");
  };

  return (
    <div className="page merchant-page merchant-create-page">
      <header className="page-header merchant-page-header">
        <div>
          <a className="merchant-back-link" href="#/merchant/orders">
            ← Все заявки
          </a>
          <h1>Новая заявка</h1>
        </div>
      </header>

      {serverError && (
        <div className="alert alert-warning merchant-create-alert" role="alert">
          {serverError}
        </div>
      )}
      <form className="merchant-create-form" onSubmit={handleSubmit} noValidate>
        <div className="merchant-create-switch" role="radiogroup" aria-label="Направление заявки">
          <button disabled={locked} type="button" role="radio" aria-checked={!isIn} className={`merchant-create-switch-option ${!isIn ? "active" : ""}`} onClick={() => patch({side: "out"})}>Выплата RUB</button>
          <button disabled={locked} type="button" role="radio" aria-checked={isIn} className={`merchant-create-switch-option ${isIn ? "active" : ""}`} onClick={() => patch({side: "in", requisite: ""})}>Пополнение USDT</button>
        </div>
        <div className="merchant-create-grid">
          {isIn && (
            <div className="merchant-create-amount-mode">
              <span className="merchant-create-label">Сумма заявки</span>
              <div className="merchant-create-switch" role="radiogroup" aria-label="Вид суммы">
                <button disabled={locked} type="button" role="radio" aria-checked={!isRange} className={`merchant-create-switch-option ${!isRange ? "active" : ""}`} onClick={() => pickAmountMode("exact")}>Точная сумма</button>
                <button disabled={locked} type="button" role="radio" aria-checked={isRange} className={`merchant-create-switch-option ${isRange ? "active" : ""}`} onClick={() => pickAmountMode("range")}>Диапазон</button>
              </div>
            </div>
          )}

          {isRange && (
            <label>
              <span>Сумма от, ₽</span>
              <input disabled={locked}
                type="text"
                inputMode="decimal"
                value={values.amountMin}
                placeholder="50000"
                aria-invalid={errors.amountMin ? true : undefined}
                onChange={(event) => {
                  patch({amountMin: event.target.value});
                  clearError("amountMin");
                }}
              />
              {errors.amountMin ? (
                <span className="merchant-create-error" role="alert">{errors.amountMin}</span>
              ) : (
                <span className="merchant-create-hint">Нижняя граница диапазона</span>
              )}
            </label>
          )}

          <label>
              <span>{isRange ? "Сумма до, ₽" : isIn ? "Сумма пополнения, ₽" : "Сумма выплаты, ₽"}</span>
            <input disabled={locked}
              type="text"
              inputMode="decimal"
              value={values.amount}
              placeholder={isRange ? "55000" : "1000.50"}
              aria-invalid={errors.amount ? true : undefined}
              onChange={(event) => {
                patch({amount: event.target.value});
                clearError("amount");
              }}
            />
            {errors.amount ? (
              <span className="merchant-create-error" role="alert">{errors.amount}</span>
            ) : (
              <span className="merchant-create-hint">
                {isRange ? "Верхняя граница диапазона" : "Рубли, до двух знаков после запятой"}
              </span>
            )}
          </label>

          <label>
            <span>Банк</span>
            <select disabled={locked}
              value={values.bank}
              onChange={(event) => patch({
                bank: event.target.value as MerchantCreateOrderBank,
              })}
            >
              <option value="any">Любой</option>
              <option value="sber">Сбербанк</option>
              <option value="tinkoff">Т-Банк</option>
              <option value="sbp">СБП</option>
            </select>
            <span className="merchant-create-hint">
              Ограничение для исполнителя заявки
            </span>
          </label>

          {isIn && (
            <label>
              <span>Количество переводов</span>
              <input disabled={locked}
                type="text"
                inputMode="numeric"
                value={values.transferCount}
                placeholder="1"
                aria-invalid={errors.transferCount ? true : undefined}
                onChange={(event) => {
                  patch({transferCount: event.target.value});
                  clearError("transferCount");
                  clearError("minTransfer");
                }}
              />
              {errors.transferCount ? (
                <span className="merchant-create-error" role="alert">{errors.transferCount}</span>
              ) : (
                <span className="merchant-create-hint">Сколько самое большее, от 1 до 20; трейдер может выдать и меньше реквизитов</span>
              )}
            </label>
          )}

          {needsMinTransfer && (
            <label>
              <span>Минимальная сумма одного перевода, ₽</span>
              <input disabled={locked}
                type="text"
                inputMode="decimal"
                value={values.minTransfer}
                placeholder="30000"
                aria-invalid={errors.minTransfer ? true : undefined}
                onChange={(event) => {
                  patch({minTransfer: event.target.value});
                  clearError("minTransfer");
                }}
              />
              {errors.minTransfer ? (
                <span className="merchant-create-error" role="alert">{errors.minTransfer}</span>
              ) : (
                <span className="merchant-create-hint">Каждый перевод будет не меньше этой суммы</span>
              )}
            </label>
          )}

          {termsHint && (
            <p className="merchant-create-terms-hint" role="status" aria-atomic="true">{termsHint}</p>
          )}

          {!isIn && <div className="merchant-create-requisite">
            <span className="merchant-create-label">Реквизит</span>
            <div className="merchant-create-switch" role="radiogroup" aria-label="Тип реквизита">
              <button
                type="button"
                disabled={locked}
                role="radio"
                aria-checked={isCard}
                className={`merchant-create-switch-option ${isCard ? "active" : ""}`}
                onClick={() => pickRequisiteType("card")}
              >
                Карта
              </button>
              <button
                type="button"
                disabled={locked}
                role="radio"
                aria-checked={!isCard}
                className={`merchant-create-switch-option ${!isCard ? "active" : ""}`}
                onClick={() => pickRequisiteType("phone")}
              >
                Телефон (СБП)
              </button>
            </div>
            <div className="merchant-create-requisite-field">
                <input disabled={locked}
                type="text"
                inputMode={isCard ? "numeric" : "tel"}
                className="merchant-create-requisite-input"
                value={values.requisite}
                placeholder={isCard ? "2200 0000 0000 0000" : "+79991234567"}
                aria-label={isCard ? "Номер карты получателя" : "Телефон получателя"}
                aria-invalid={errors.requisite ? true : undefined}
                onChange={(event) => {
                  patch({requisite: event.target.value});
                  clearError("requisite");
                }}
              />
              {errors.requisite ? (
                <span className="merchant-create-error" role="alert">{errors.requisite}</span>
              ) : (
                <span className="merchant-create-hint">
                  {isCard ? "16 цифр, пробелы можно" : "Телефон, привязанный к СБП"}
                </span>
              )}
            </div>
          </div>}

          {!isIn && <label>
            <span>
              ФИО получателя <span className="merchant-create-optional">— необязательно</span>
            </span>
            <input disabled={locked}
              type="text"
              value={values.holderName}
              placeholder="Иванов Иван Иванович"
              onChange={(event) => patch({holderName: event.target.value})}
            />
          </label>}

          {!isIn && <label>
            <span>
              Банк получателя <span className="merchant-create-optional">— необязательно</span>
            </span>
            <input disabled={locked}
              type="text"
              value={values.recipientBank}
              placeholder="Если банка нет в списке"
              onChange={(event) => patch({recipientBank: event.target.value})}
            />
          </label>}
        </div>

        {quote && (
          <div className="merchant-create-quote" role="status" aria-atomic="true">
            <dl className="merchant-create-quote-values">
              <div>
                <dt>Курс</dt>
                <dd>{quote.rate} ₽/USDT</dd>
              </div>
              <div>
                <dt>К зачислению</dt>
                <dd className="merchant-create-quote-credit">
                  {quoteIsRange && quote.usdtAtomicMin
                    ? `от ${formatAtomicUSDT(quote.usdtAtomicMin)} до ${formatAtomicUSDT(quote.usdtAtomic)} USDT`
                    : `${formatAtomicUSDT(quote.usdtAtomic)} USDT`}
                </dd>
              </div>
            </dl>
            {quoteIsRange && (
              <p className="merchant-create-quote-note">Точная сумма — по выбору трейдера.</p>
            )}
            <p className="merchant-create-quote-expiry">
              Курс действует до {new Date(quote.expiresAt).toLocaleTimeString()}
            </p>
          </div>
        )}

        <div className="merchant-create-actions">
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? (isIn ? "Проверяем курс…" : "Создаём…") : retrying ? "Повторить создание" : quote ? "Принять курс и создать" : (isIn ? "Получить курс" : "Создать заявку")}
          </button>
        </div>
      </form>
    </div>
  );
}

export function formatAtomicUSDT(value: string): string {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return "—";
  const padded = normalized.padStart(7, "0");
  return `${padded.slice(0, -6)}.${padded.slice(-6)}`;
}

export function portalInEditTransition(submitting: boolean, ambiguous: boolean): {clearQuote: boolean; regenerateRequestId: boolean} {
  const editable = !submitting && !ambiguous;
  return {clearQuote: editable, regenerateRequestId: editable};
}

export function validateCreateOrder(
  values: MerchantCreateOrderValues,
): MerchantCreateErrors {
  const errors: MerchantCreateErrors = {};
  const amount = values.amount.trim();
  if (!amount) {
    errors.amount = "Укажите сумму заявки.";
  } else if (!AMOUNT_PATTERN.test(amount)) {
    errors.amount = "Сумма — число с не более чем двумя знаками после запятой.";
  } else if (Number(amount.replace(",", ".")) <= 0) {
    errors.amount = "Сумма должна быть больше нуля.";
  }

  const requisite = values.requisite.trim().replace(/\s/g, "");
  // Диапазон и переводы — только у пополнения. Ошибка самой суммы остаётся за
  // полем суммы, остальные проверки добавляют свои поля.
  if (values.side === "in") return {...validatePortalInTerms(values), ...errors};
  if (!requisite) {
    errors.requisite = values.requisiteType === "card"
      ? "Укажите номер карты получателя."
      : "Укажите телефон получателя.";
  } else if (values.requisiteType === "card" && !CARD_PATTERN.test(requisite)) {
    errors.requisite = "Номер карты — 16 цифр.";
  } else if (values.requisiteType === "phone" && !PHONE_PATTERN.test(requisite)) {
    errors.requisite = "Телефон в формате +79991234567.";
  }

  return errors;
}

/**
 * Форма отдаёт то, что набрал человек; тело запроса собирается здесь: рубли
 * переводятся в копейки, реквизит раскладывается по своему полю, пустые
 * необязательные поля не отправляются вовсе.
 */
export function toCreateOrderBody(
  clientRequestId: string,
  values: MerchantCreateOrderValues,
): MerchantCreateOrderBody | null {
  const amount = rublesToKopecks(values.amount);
  if (amount == null) return null;
  const requisite = values.requisite.trim().replace(/\s/g, "");
  const holderName = values.holderName.trim();
  const recipientBank = values.recipientBank.trim();
  return {
    clientRequestId,
    amount,
    bank: values.bank,
    clientCard: values.requisiteType === "card" ? requisite : null,
    clientPhone: values.requisiteType === "phone" ? requisite : null,
    clientFio: holderName ? holderName : null,
    clientBank: recipientBank ? recipientBank : null,
  };
}

/**
 * ID сохраняется при повторной отправке. Новые параметры получают новый ID
 * только пока результат предыдущего создания не остаётся неопределённым.
 */
export function newClientRequestId(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  // Совсем старый браузер без randomUUID: uuid v4 из случайных байт.
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function MerchantCreateOrderPage(): React.JSX.Element {
  const {token} = useAuth();
  const [clientRequestId, setClientRequestId] = useState(newClientRequestId);
  const [submitting, setSubmitting] = useState(false);
  const [ambiguous, setAmbiguous] = useState(false);
  const [serverError, setServerError] = useState("");
  const [quote, setQuote] = useState<MerchantPortalInQuote | null>(null);

  const handleSubmit = (values: MerchantCreateOrderValues): void => {
    if (submitting) return;
    const body = toCreateOrderBody(clientRequestId, values);
    if (!body) {
      setServerError("Проверьте поля формы и попробуйте снова.");
      return;
    }
    if (values.side === "in") {
      const terms = toPortalInTerms(values);
      if (!terms) {
        setServerError("Проверьте поля формы и попробуйте снова.");
        return;
      }
      if (quote) {
        setSubmitting(true);
        createMerchantPortalIn(token, {clientRequestId, amount: body.amount, bank: body.bank, ...terms, quoteId: quote.quoteId})
          .then((created) => navigate(`#/merchant/orders/${created.orderId}`))
          .catch((error: unknown) => {
            setServerError(formatMerchantApiError(error, "create"));
            setSubmitting(false);
            if (error instanceof ApiError && (error.status === 409 || error.status === 400)) {
              setQuote(null);
              setAmbiguous(false);
            } else {
              setAmbiguous(true);
            }
          })
        return;
      }
      setSubmitting(true); setServerError("");
      quoteMerchantPortalIn(token, {clientRequestId, amount: body.amount, bank: body.bank, ...terms})
        .then((nextQuote) => { setQuote(nextQuote); setSubmitting(false); })
        .catch((error: unknown) => { setServerError(formatMerchantApiError(error, "create")); setSubmitting(false); });
      return;
    }
    setSubmitting(true);
    setServerError("");
    createMerchantOrder(token, body)
      .then((created) => {
        navigate(`#/merchant/orders/${created.orderId}`);
      })
      .catch((error: unknown) => {
        // A later rejection cannot disprove an earlier request's success.
        // Keep its exact body/ID locked until the idempotent retry resolves it.
        const uncertain = ambiguous || !(error instanceof ApiError && (error.status === 400 || error.status === 409));
        setAmbiguous(uncertain);
        setServerError(uncertain
          ? "Не удалось подтвердить создание заявки. Повторите отправку — параметры сохранены."
          : formatMerchantApiError(error, "create"));
        setSubmitting(false);
      });
  };

  return (
    <MerchantCreateOrderForm
      onSubmit={handleSubmit}
      submitting={submitting}
      serverError={serverError}
      quote={quote}
      locked={submitting || ambiguous}
      retrying={ambiguous}
      onValuesChange={() => {
        const transition = portalInEditTransition(submitting, ambiguous);
        if (!transition.clearQuote) return;
        setQuote(null);
        // Editing starts a new logical attempt; an unchanged form keeps its
        // ID so an ambiguous create can be retried idempotently.
        if (transition.regenerateRequestId) setClientRequestId(newClientRequestId());
      }}
    />
  );
}
